<script setup>
/**
 * FormControlProductTypesShipping
 * Handles dynamic delivery fee configurations mapped across structural regions.
 * Features automated normalization algorithms to combine multi-tier duplicate location references
 * into unified UI input rows while synchronizing state parameters across hidden backend variants.
 */
import {ref, computed, watch, onMounted} from 'vue'
import {useFormStore} from '@/stores/form'
import api from '@/services/api'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
})

// Pinia Store
const formStore = useFormStore(props.formId)

// --- State Layers ---

const locations = ref([])
const hiddenLocationIds = ref([])

// --- Synchronous Computeds ---

const typeId = computed(() => props.currentData?.typeId)

/**
 * Filtered Locations Computed
 * FIX: Replaces the invalid combined v-for/v-if template syntax.
 * Separates loop processing metrics from evaluation rules safely to prevent Vue 2.7/3 compilation crashes.
 */
const visibleLocations = computed(() => {
    const activeLocations = locations.value || []
    const hiddenIds = hiddenLocationIds.value || []
    return activeLocations.filter(loc => !hiddenIds.includes(loc.id))
})

// --- Methods ---

/**
 * Parses and formats background geographic data mappings.
 * Generates protective reactive object keys to support deep tracking loops.
 */
const setShippingData = () => {
    if (!locations.value || locations.value.length === 0) return

    const shippingPayloadTree = {}
    const historicalShippingState = props.currentData?.shipping

    // Standardize baseline values and enforce normalization for legacy string configurations
    locations.value.forEach((loc) => {
        let inUse = false
        let shippingFee = ''

        if (historicalShippingState && historicalShippingState[loc.id]) {
            const locState = historicalShippingState[loc.id]
            inUse = locState.in_use === '1' || (!!locState.in_use && locState.in_use !== '0')
            shippingFee = locState.shipping_fee || ''
        }

        shippingPayloadTree[loc.id] = {
            in_use: inUse,
            shipping_fee: shippingFee
        }
    })

    // Enforce root structure reactivity safely
    if (!props.currentData.shipping) {
        props.currentData.shipping = shippingPayloadTree
    } else {
        Object.keys(shippingPayloadTree).forEach((key) => {
            props.currentData.shipping[key] = shippingPayloadTree[key]
        })
    }

    // Push state metrics straight down into active form state managers
    formStore.presetData({
        name: 'shipping',
        value: props.currentData.shipping
    })
}

/**
 * Queries regional data endpoints asynchronously.
 * Calculates structural deduplication lists to keep input forms clean.
 */
const fetchLocationsCatalog = async () => {
    try {
        const response = await api.get('api/products-shipping-fee-data', {
            params: {typeId: typeId.value}
        })

        const fetchedData = response?.data || []
        locations.value = fetchedData

        // Reset duplicate location filters
        const recordedTitles = []
        const excludedIds = []

        fetchedData.forEach((item) => {
            if (recordedTitles.includes(item.location)) {
                excludedIds.push(item.id)
            } else {
                recordedTitles.push(item.location)
            }
        })

        hiddenLocationIds.value = excludedIds

        // Hydrate pricing and usage matrices
        setShippingData()

    } catch (err) {
        console.error('Failed to resolve layout delivery regions options dataset:', err)
    }
}

/**
 * Synchronizes sibling fee rates across matching location headers.
 * FIX: Extracts strict string properties instead of comparing entire object matrices.
 */
const handleLocationStateSync = (locationId) => {
    const originRecord = locations.value.find(obj => obj.id === locationId)
    const originShippingData = props.currentData?.shipping?.[locationId]

    if (!originRecord || !originShippingData) return

    const targetLocationName = originRecord.location

    // Broad broadcast update to identical backend tracking matrices
    locations.value.forEach((obj) => {
        if (obj.location === targetLocationName && obj.id !== locationId) {
            props.currentData.shipping[obj.id] = {
                in_use: originShippingData.in_use,
                shipping_fee: originShippingData.shipping_fee
            }
        }
    })
}

/**
 * Activates check parameters if text inputs receive manual click triggers.
 */
const forceLocationActivation = (locationId) => {
    if (props.currentData?.shipping?.[locationId]) {
        props.currentData.shipping[locationId].in_use = true
        handleLocationStateSync(locationId)
    }
}

// --- Structural Watchers & Lifecycles ---

watch(typeId, async () => {
    await fetchLocationsCatalog()
})

onMounted(async () => {
    if (props.field) {
        props.field.tooltip =
            "Assign default variable rates to all products of this type." +
            "<br>Shipping fees assigned to individual products of this type will override these settings." +
            "<br>Shipping fees set here will not be charged if the order is outside of the deliver location's minimum/maximum threshold." +
            "<br>For more information go to: eCommerce / Shipping."
    }

    await fetchLocationsCatalog()
})
</script>

<template>
    <div class="form-control-product-types-shipping-wrapper">

        <div
            v-for="location in visibleLocations"
            :key="location.id"
            class="d-flex align-items-center mb-2 regional-shipping-row"
        >
            <template v-if="currentData.shipping && currentData.shipping[location.id]">

                <input
                    :id="'shipping-' + location.id"
                    v-model="currentData.shipping[location.id].in_use"
                    type="checkbox"
                    class="form-check-input"
                    @change="handleLocationStateSync(location.id)"
                >

                <input
                    v-model="currentData.shipping[location.id].shipping_fee"
                    :readonly="!currentData.shipping[location.id].in_use"
                    class="ms-2 form-control form-control-sm w-auto text-end"
                    size="7"
                    type="text"
                    placeholder="0.00"
                    @click="forceLocationActivation(location.id)"
                    @input="handleLocationStateSync(location.id)"
                >

                <label :for="'shipping-' + location.id" class="ms-2 small mb-0" role="button">
                    {{ location.location }}
                </label>

            </template>
        </div>

    </div>
</template>

<style scoped>
/* Aligns form field entries smoothly across custom row groups */
.regional-shipping-row input[type="text"] {
    font-family: monospace;
}
</style>